// ============================================================================
// DetailWidget Component
// Zentrales Produkt/Test Detail Widget mit mehreren Panels:
// 1) Stammdaten & Schwellenwerte
// 2) Jira Tickets Übersicht
// 3) Klickweg (Schritte) mit Meta Informationen
// 4) SOC ToDos (Placeholder)
// 5) Admin Funktionen (Placeholder)
// Enthält: Editing Workflow, Dummy-Daten Fetch, Refresh Handling, Link Parsing
// ============================================================================

import React, {memo} from "react";
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Tooltip } from 'react-tooltip';
import AppSpinner from './AppSpinner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import Quill from 'quill';
// Backend API wrapper (prepared for future real endpoints)
import { detailWidgetApi } from '../api/detailWidgetApi';
// import { cloudflareZonesAPI } from "../http-common";     // Removed unused import
import '../styles/ProductOverviewWidget.css';
import '../styles/Detailwidget.css';
import 'react-tooltip/dist/react-tooltip.css';
import 'quill/dist/quill.core.css';
// Quill Link sanitize override: ensure scheme for plain domains (e.g., google.de)
const QuillLink = Quill.import('formats/link');
const originalSanitize = QuillLink && QuillLink.sanitize ? QuillLink.sanitize : (v) => v;
QuillLink.sanitize = function (url) {
    // Quill 2: original sanitize is a static method that reads this.PROTOCOL_WHITELIST
    // -> must be invoked with QuillLink as `this`
    const value = originalSanitize.call(QuillLink, url || '');
    // Allow mailto: and http/https. For bare domains, prepend https://
    const hasScheme = /^https?:\/\//i.test(value) || /^mailto:/i.test(value);
    if (!hasScheme && typeof value === 'string' && value.trim() !== '') {
        // Remove leading slashes to avoid //domain
        const cleaned = value.replace(/^\/+/, '');
        return `https://${cleaned}`;
    }
    return value;
};

// Quill modules for editable overlays: ensure clipboard links get proper scheme
const editableQuillModules = {
    toolbar: true,
    clipboard: {
        matchers: [
            ['A', (node, delta) => {
                try {
                    const href = node.getAttribute('href') || '';
                    const hasScheme = /^https?:\/\//i.test(href) || /^mailto:/i.test(href);
                    if (!hasScheme && href.trim() !== '') {
                        const cleaned = href.replace(/^\/+/, '');
                        node.setAttribute('href', `https://${cleaned}`);
                    }
                } catch(e) { /* ignore */ }
                return delta;
            }]
        ]
    }
};

/**
 * Normalisiert einen Jira-Status-String (englisch/deutsch, verschiedene Schreibweisen)
 * auf eine kleine Anzahl kanonischer Kategorien fuer die CSS-Modifier-Klassen
 * .detailTicketStatus--<kategorie>. Fallback: 'unknown'.
 */
const TICKET_STATUS_MAP = [
    { key: 'done',        patterns: ['done', 'closed', 'resolved', 'complete', 'completed', 'finished', 'erledigt', 'geschlossen', 'abgeschlossen', 'gel\u00f6st', 'fertig'] },
    { key: 'in-progress', patterns: ['in progress', 'progress', 'in arbeit', 'in bearbeitung', 'in umsetzung', 'wip', 'doing', 'in test', 'testing'] },
    { key: 'review',      patterns: ['review', 'code review', 'in review', 'pr review', 'peer review', 'im review'] },
    { key: 'analysis',    patterns: ['analysis', 'analyse', 'triage', 'investigation', 'investigating'] },
    { key: 'blocked',     patterns: ['blocked', 'blockiert', 'on hold', 'hold', 'wartet auf', 'waiting', 'pending', 'zur\u00fcckgestellt'] },
    { key: 'open',        patterns: ['open', 'offen', 'new', 'neu', 'to do', 'todo', 'backlog', 'reopened', 'wiedereroeffnet', 'wiedereroffnet', 'ready'] },
];

function normalizeTicketStatus(status) {
    const s = (status || '').toLowerCase().trim();
    if (!s) return 'unknown';
    for (const { key, patterns } of TICKET_STATUS_MAP) {
        if (patterns.some(p => s === p || s.includes(p))) return key;
    }
    return 'unknown';
}


const DetailWidget = (props) => {
    const { drawnGraphs, receiveIDToken, handleNotify } = props; // Zentrale Props vom Dashboard
       
    /***
     *     __  ___      ___  ___  __  
     *    /__`  |   /\   |  |__  /__` 
     *    .__/  |  /~~\  |  |___ .__/ 
     *                                
     */

    // ------------------------------------------------------------------------
    // Local State
    // ------------------------------------------------------------------------
    const [configMenuStatus, setConfigMenuStatus] = React.useState(false);  // Konfig Menu Sichtbarkeit
    const [isLoaded, setIsLoaded] = React.useState(false);                  // Daten Ladeindikator
    const [loadReason, setLoadReason] = React.useState('initial');          // initial | refresh
    const [detailData, setDetailData] = React.useState(null);               // Vollständiger Detail-Dummy / API Response
    const detailInfo = detailData && Array.isArray(detailData.detailinfos) ? detailData.detailinfos[0] : null; // Erstes Detail Objekt
    const [hasError] = React.useState(false);                               // Fehlerstatus (derzeit nicht genutzt)

    // Bearbeitungsmodus & Formularwerte
    const [isEditing, setIsEditing] = React.useState(false);
    const [showTestFreqOverlay, setShowTestFreqOverlay] = React.useState(false); // Overlay Sichtbarkeit Testhäufigkeit
    const [showDeleteProductOverlay, setShowDeleteProductOverlay] = React.useState(false); // Overlay Sichtbarkeit Produkt löschen
    const [showDeleteTestOverlay, setShowDeleteTestOverlay] = React.useState(false); // Overlay Sichtbarkeit Test löschen
    const [showReloadOverlay, setShowReloadOverlay] = React.useState(false); // Overlay: Hinweis zum Neuladen nach Panel-1 Save
    const [testFreqValue, setTestFreqValue] = React.useState(''); // Eingabewert (ganze Zahl Minuten)
    const [adminPasswordValue, setAdminPasswordValue] = React.useState(''); // Admin Passwort Eingabe (wird später ans Backend gesendet)
    const [showAdminPassword, setShowAdminPassword] = React.useState(false); // Toggle Sichtbarkeit Passwort
    const testFreqInputRef = React.useRef(null);
    const deletePwInputRef = React.useRef(null);
    const deleteTestPwInputRef = React.useRef(null);
    const [editableValues, setEditableValues] = React.useState({
        productID:'', testID:'', productName:'', testName:'', testOwner:'', thresholdOkay:'', thresholdWarn:'', thresholdCrit:''
    });

    // Einheitliche ID-Ermittlung für alle API Calls
    // Priorität: ausgewählter Graph (props.selectedGraph) > geladene detailInfo
    // Rückgabe: { productID, testID } oder null, falls unvollständig
    const getCurrentIDs = React.useCallback(() => {
        if (props.selectedGraph) {
            const matchSel = drawnGraphs.find(g => g.productName === props.selectedGraph.productName && g.testName === props.selectedGraph.testName);
            if (matchSel && matchSel.productID && matchSel.testID) {
                return { productID: matchSel.productID, testID: matchSel.testID };
            }
        }
        if (detailInfo && detailInfo.productID && detailInfo.testID) {
            return { productID: detailInfo.productID, testID: detailInfo.testID };
        }
        return null; // Keine gültigen IDs vorhanden
    }, [props.selectedGraph, drawnGraphs, detailInfo]);
    // Quill Editor Overlay (Panels 3, 4 & Logs in Panel 5)
    const [editorOverlayType, setEditorOverlayType] = React.useState(null); // 'klickweg' | 'handlungs' | 'logs' | null
    const [editorDelta, setEditorDelta] = React.useState(null); // Aktueller Delta Inhalt
    const detailFormRef = React.useRef(null);                               // Ref für Formularauslese beim Speichern

    // Aktives Panel (Tabs 1..5)
    const [activePanel, setActivePanel] = React.useState(1);
    const handleSwitchPanel = (panel) => {
        setActivePanel(panel);
    }


  /***********************************************************************************************************
  /********************************************* DATENABRUF **************************************************
  /********************************************************************************************************** */  

// ------------------------------------------------------------------------
// Reine API Nutzung – Dummy entfernt. Bei Fehler: Notify Alert.
// Erfolgreiches Laden wird nicht mehr aktiv "notified" (Anforderung)
const fetchDetailWidgetData = React.useCallback(async (productID, testID, widgetId) => {
    const token = typeof receiveIDToken === 'function' ? receiveIDToken() : '';
    try {
        const resp = await detailWidgetApi.fetchDetail(token, { productID, testID, widgetId });
        setDetailData(resp.data);
        setIsLoaded(true);
        // handleNotify && handleNotify({ type:'success', message:'Detaildaten geladen.' });
        return resp.data;
    } catch(err) {
        setIsLoaded(true);
        handleNotify && handleNotify({ type:'alert', message:'Detaildaten Abruf fehlgeschlagen.' });
        return null;
    }
}, [receiveIDToken, handleNotify]);


    /***
     *          __   ___     ___  ___  ___  ___  __  ___  __  
     *    |  | /__` |__     |__  |__  |__  |__  /  `  |  /__` 
     *    \__/ .__/ |___    |___ |    |    |___ \__,  |  .__/ 
     *                                                        
     */

    // ------------------------------------------------------------------------
    // Effekt: Re-Fetch bei Auswahländerung des Graphen
    // Stellt sicher, dass Detaildaten zur aktuellen Auswahl geladen sind.
    // ------------------------------------------------------------------------
    React.useEffect(() => {
        // Auswahlprüfung
        if(!props.selectedGraph) return;
        const match = drawnGraphs.find(g => g.productName === props.selectedGraph.productName && g.testName === props.selectedGraph.testName);
        if(!match) return;
        const productID = match.productID;
        const testID = match.testID;
        if(!productID || !testID){
            handleNotify && handleNotify({type:'alert', message:'Fehlende IDs – Auswahl unvollständig.'});
            return;
        }
        // Optionales Debug-Log bei Auswahländerung (bewusst auskommentiert)
        // eslint-disable-next-line no-console
        // console.log('[DetailWidget] Auswahl geändert ->', { productID, testID });
        // BUGFIX: Loading-Zustand aktiv setzen, damit der Spinner + "Lade Daten ..."
        // sofort beim Info-Button-Klick sichtbar sind (Fetch dauert 2-3 s).
        // Ohne dieses Set bliebe der Text auf "Bitte Info Button im Test klicken".
        setLoadReason('refresh');
        setIsLoaded(false);
        fetchDetailWidgetData(productID, testID, props.id)
            .catch(() => { /* Fehler wird bereits intern per notify behandelt */ });
    }, [props.selectedGraph, drawnGraphs, props.id, fetchDetailWidgetData, handleNotify]);

    // Edit-Modus nur beenden, wenn sich die Graph-Auswahl tatsächlich geändert hat
    const prevSelectedKeyRef = React.useRef(null);
    React.useEffect(() => {
        const key = props.selectedGraph ? `${props.selectedGraph.productName}::${props.selectedGraph.testName}` : null;
        const prev = prevSelectedKeyRef.current;
        if(prev !== null && key !== prev && isEditing){
            setIsEditing(false);
            setEditableValues({ productID:'', testID:'', productName:'', testName:'', testOwner:'', thresholdOkay:'', thresholdWarn:'', thresholdCrit:'' });
        }
        prevSelectedKeyRef.current = key;
    }, [props.selectedGraph, isEditing]);

    // Wenn Edit-Modus aktiv ist und der ausgewählte Graph nicht mehr vorhanden ist,
    // den Edit-Modus automatisch beenden, damit beim nächsten Graph nicht weiterhin Edit aktiv ist.
    React.useEffect(() => {
        if(!props.selectedGraph) return;
        const match = drawnGraphs.find(g => g.productName === props.selectedGraph.productName && g.testName === props.selectedGraph.testName);
        if(!match && isEditing) {
            setIsEditing(false);
            setEditableValues({ productID:'', testID:'', productName:'', testName:'', testOwner:'', thresholdOkay:'', thresholdWarn:'', thresholdCrit:'' });
        }
    }, [props.selectedGraph, drawnGraphs, isEditing]);


    
    /***
     *     ___            __  ___    __        __  
     *    |__  |  | |\ | /  `  |  | /  \ |\ | /__` 
     *    |    \__/ | \| \__,  |  | \__/ | \| .__/ 
     *                                             
     */

     
    // ------------------------------------------------------------------------
    // UI / Interaction Handler
    // ------------------------------------------------------------------------
    // Öffnet/schließt das Konfigurationsmenü (Widget-Kopfzeile)
    const toggleConfigMenuStatus = () => setConfigMenuStatus(!configMenuStatus);

    // toggleDefaultTimerange removed (unused)

    // Aktiviert den Bearbeitungsmodus und übernimmt initiale Werte
    const enableEditing = (detailInfo) => {
        if(!isEditing && detailInfo){
            setEditableValues({
                productID: detailInfo.productID || '',
                testID: detailInfo.testID || '',
                productName: detailInfo.productName || '',
                testName: detailInfo.testName || '',
                testOwner: detailInfo.testOwner || '',
                thresholdOkay: detailInfo.thresholds?.okay ?? '',
                thresholdWarn: detailInfo.thresholds?.warn ?? '',
                thresholdCrit: detailInfo.thresholds?.crit ?? ''
            });
            setIsEditing(true);
            if(props.handleNotify){
                props.handleNotify({type:'info', message:'Bearbeitungsmodus aktiviert.'});
            }
        }
    }

    // Bricht Bearbeitung ab und setzt Formular zurück
    const cancelEditing = () => {
        setIsEditing(false);
    setEditableValues({ productID:'', testID:'', productName:'', testName:'', testOwner:'', thresholdOkay:'', thresholdWarn:'', thresholdCrit:'' });
        if(props.handleNotify){
            props.handleNotify({type:'info', message:'Bearbeitung abgebrochen.'});
        }
    }

    // Generischer onChange Handler für Edit-Felder
    const onEditableChange = (e) => {
        const { name, value } = e.target;
        let nextValue = value;
        if(name === 'thresholdOkay' || name === 'thresholdWarn' || name === 'thresholdCrit'){
            // Nur Ziffern zulassen, Länge auf 4 begrenzen
            nextValue = (value || '').replace(/\D/g, '').slice(0, 4);
        }
        setEditableValues(prev => ({...prev, [name]: nextValue}));
    }

    // Overlay Öffnen für Testhäufigkeit
    const openTestFreqOverlay = React.useCallback(() => {
        const current = (detailInfo && detailInfo.testinterval) ? String(detailInfo.testinterval) : '';
        setTestFreqValue(current);
        setShowTestFreqOverlay(true);
    }, [detailInfo]);

    const closeTestFreqOverlay = React.useCallback(() => {
        setShowTestFreqOverlay(false);
    }, []);

    // Overlay Öffnen für Produkt löschen
    const openDeleteProductOverlay = React.useCallback(() => {
        // Passwort beim Öffnen zurücksetzen, Sichtbarkeit verbergen
        setAdminPasswordValue('');
        setShowAdminPassword(false);
        setShowDeleteProductOverlay(true);
    }, []);

    const closeDeleteProductOverlay = React.useCallback(() => {
        setShowDeleteProductOverlay(false);
    }, []);

    const openDeleteTestOverlay = React.useCallback(() => {
        setAdminPasswordValue('');
        setShowAdminPassword(false);
        setShowDeleteTestOverlay(true);
    }, []);

    const closeDeleteTestOverlay = React.useCallback(() => {
        setShowDeleteTestOverlay(false);
    }, []);

    const openReloadOverlay = React.useCallback(() => {
        setShowReloadOverlay(true);
    }, []);
    const closeReloadOverlay = React.useCallback(() => {
        setShowReloadOverlay(false);
    }, []);

    const handleTestFreqChange = (e) => {
        // nur Ziffern, maximal 4 Zeichen
        const raw = e.target.value || '';
        const v = raw.replace(/\D/g, '').slice(0, 4);
        if(/^\d{0,4}$/.test(v)) {
            setTestFreqValue(v);
        }
    };
    const handleAdminPasswordChange = (e) => {
        // Keine Einschränkung außer Trimmen; Backend Validierung später
        setAdminPasswordValue(e.target.value);
    };
    const toggleAdminPasswordVisibility = () => {
        setShowAdminPassword(v => !v);
    }

    /** Speichert Test-Intervall (Admin) */
    const handleTestFreqSave = () => {
        if(testFreqValue === '' || !/^\d+$/.test(testFreqValue)) {
            handleNotify && handleNotify({type:'alert', message:'Bitte eine ganze Zahl für das Intervall eingeben.'});
            return;
        }
        if(adminPasswordValue.trim() === '') {
            handleNotify && handleNotify({type:'alert', message:'Admin Passwort darf nicht leer sein.'});
            return;
        }
        // IDs zentral ermitteln
        const { productID, testID } = getCurrentIDs();
        const token = receiveIDToken && receiveIDToken();
        detailWidgetApi.updateTestFrequency(token, { productID, testID, intervalMinutes: parseInt(testFreqValue,10), adminPassword: adminPasswordValue, userName: props.userName || '' })
            .then(resp => {
                setDetailData(d => {
                    if(!d || !Array.isArray(d.detailinfos) || d.detailinfos.length === 0) return d;
                    const clone = { ...d };
                    clone.detailinfos = [...clone.detailinfos];
                    clone.detailinfos[0] = { ...clone.detailinfos[0], testinterval: testFreqValue };
                    return clone;
                });
                handleNotify && handleNotify({ type:'success', message:'Intervall aktualisiert.' });
                closeTestFreqOverlay();
            })
            .catch(err => {
                handleNotify && handleNotify({ type:'alert', message:'Intervall Update fehlgeschlagen.' });
            });
    };

    /** Löscht Produkt (Admin) */
    const handleProductDelete = () => {
        if(adminPasswordValue.trim() === '') {
            handleNotify && handleNotify({type:'alert', message:'Admin Passwort darf nicht leer sein.'});
            return;
        }
        const { productID } = getCurrentIDs();
        const token = receiveIDToken && receiveIDToken();
        detailWidgetApi.deleteProduct(token, { productID, adminPassword: adminPasswordValue, userName: props.userName || '' })
            .then(() => {
                handleNotify && handleNotify({ type:'success', message:'Produkt gelöscht.' });
                closeDeleteProductOverlay();
            })
            .catch(err => {
                handleNotify && handleNotify({ type:'alert', message:'Produkt löschen fehlgeschlagen.' });
            });
    };

    /** Löscht Test (Admin) */
    const handleTestDelete = () => {
        if(adminPasswordValue.trim() === '') {
            handleNotify && handleNotify({type:'alert', message:'Admin Passwort darf nicht leer sein.'});
            return;
        }
        const { productID, testID } = getCurrentIDs();
        const token = receiveIDToken && receiveIDToken();
        detailWidgetApi.deleteTest(token, { productID, testID, adminPassword: adminPasswordValue, userName: props.userName || '' })
            .then(() => {
                handleNotify && handleNotify({ type:'success', message:'Test gelöscht.' });
                closeDeleteTestOverlay();
            })
            .catch(err => {
                handleNotify && handleNotify({ type:'alert', message:'Test löschen fehlgeschlagen.' });
            });
    };
    /**
     * Öffnet den Quill Editor Overlay
     * Typen:
     * - 'klickweg' (editierbar)
     * - 'handlungs' (editierbar)
     * - 'logs' (read-only)
     * Initialisiert Delta aus den geladenen Daten (falls vorhanden).
     */
    const openEditorOverlay = React.useCallback((type) => {
        if(type !== 'klickweg' && type !== 'handlungs' && type !== 'logs') return;
        let initialDelta = { ops: [] };
        if(type === 'klickweg') {
            const rawKW = detailData?.Klickweg;
            const opsArray = Array.isArray(rawKW) ? rawKW[0]?.ops : rawKW?.ops;
            if(Array.isArray(opsArray)) initialDelta = { ops: opsArray };
        } else if(type === 'handlungs') {
            const rawHA = detailData?.Handlungsanweisung;
            const opsArray = Array.isArray(rawHA) ? rawHA[0]?.ops : rawHA?.ops;
            if(Array.isArray(opsArray)) initialDelta = { ops: opsArray };
        } else if(type === 'logs') {
            const rawLogs = detailData?.lastLogs;
            const opsArray = Array.isArray(rawLogs) ? rawLogs[0]?.ops : rawLogs?.ops;
            if(Array.isArray(opsArray)) initialDelta = { ops: opsArray };
        }
        setEditorDelta(initialDelta);
        setEditorOverlayType(type);
    }, [detailData]);

    const closeEditorOverlay = React.useCallback(() => {
        setEditorOverlayType(null);
        setEditorDelta(null);
    }, []);

    // Speichert den aktualisierten Klickweg Delta Inhalt (Panel 3)
    const saveKlickwegDelta = React.useCallback(() => {
        if(!editorDelta) { closeEditorOverlay(); return; }
        const normalizeDeltaLinks = (deltaObj) => {
            try {
                const ops = Array.isArray(deltaObj?.ops) ? deltaObj.ops.map(op => {
                    if(op && op.attributes && typeof op.attributes.link === 'string'){
                        const href = op.attributes.link;
                        const hasScheme = /^https?:\/\//i.test(href) || /^mailto:/i.test(href);
                        if(!hasScheme && href.trim() !== ''){
                            const cleaned = href.replace(/^\/+/, '');
                            return { ...op, attributes: { ...op.attributes, link: `https://${cleaned}` } };
                        }
                    }
                    return op;
                }) : deltaObj?.ops;
                return { ...(deltaObj || {}), ops };
            } catch(e){ return deltaObj; }
        };
        const fixedDelta = normalizeDeltaLinks(editorDelta);
        const { productID, testID } = getCurrentIDs();
        const token = receiveIDToken && receiveIDToken();
        detailWidgetApi.updateKlickweg(token, { productID, testID, delta: fixedDelta, userName: props.userName || '' })
            .then(resp => {
                const updatedOps = resp?.data?.updated?.ops || fixedDelta.ops || [];
                // Optimistic Inhalt aktualisieren
                setDetailData(prev => {
                    if(!prev) return prev;
                    const clone = { ...prev };
                    clone.Klickweg = [ { ops: updatedOps } ];
                    return clone;
                });
                handleNotify && handleNotify({ type:'success', message:'Klickweg gespeichert.' });
                // Nach Speichern: frische Daten abrufen, um Meta (letzte Änderung) zu aktualisieren
                fetchDetailWidgetData(productID, testID, props.id);
                closeEditorOverlay();
            })
            .catch(err => {
                handleNotify && handleNotify({ type:'alert', message:'Klickweg speichern fehlgeschlagen.' });
            });
    }, [editorDelta, props.userName, handleNotify, closeEditorOverlay, receiveIDToken, getCurrentIDs, fetchDetailWidgetData, props.id]);

    // Speichert den aktualisierten Handlungsanweisung Delta Inhalt (Panel 4)
    const saveHandlungsDelta = React.useCallback(() => {
        if(!editorDelta) { closeEditorOverlay(); return; }
        const normalizeDeltaLinks = (deltaObj) => {
            try {
                const ops = Array.isArray(deltaObj?.ops) ? deltaObj.ops.map(op => {
                    if(op && op.attributes && typeof op.attributes.link === 'string'){
                        const href = op.attributes.link;
                        const hasScheme = /^https?:\/\//i.test(href) || /^mailto:/i.test(href);
                        if(!hasScheme && href.trim() !== ''){
                            const cleaned = href.replace(/^\/+/, '');
                            return { ...op, attributes: { ...op.attributes, link: `https://${cleaned}` } };
                        }
                    }
                    return op;
                }) : deltaObj?.ops;
                return { ...(deltaObj || {}), ops };
            } catch(e){ return deltaObj; }
        };
        const fixedDelta = normalizeDeltaLinks(editorDelta);
        const { productID, testID } = getCurrentIDs();
        const token = receiveIDToken && receiveIDToken();
        detailWidgetApi.updateHandlungsanweisung(token, { productID, testID, delta: fixedDelta, userName: props.userName || '' })
            .then(resp => {
                const updatedOps = resp?.data?.updated?.ops || fixedDelta.ops || [];
                // Optimistic Inhalt aktualisieren
                setDetailData(prev => {
                    if(!prev) return prev;
                    const clone = { ...prev };
                    clone.Handlungsanweisung = [ { ops: updatedOps } ];
                    return clone;
                });
                handleNotify && handleNotify({ type:'success', message:'Handlungsanweisung gespeichert.' });
                // Nach Speichern: frische Daten abrufen, um Meta (letzte Änderung) zu aktualisieren
                fetchDetailWidgetData(productID, testID, props.id);
                closeEditorOverlay();
            })
            .catch(err => {
                handleNotify && handleNotify({ type:'alert', message:'Handlungsanweisung speichern fehlgeschlagen.' });
            });
    }, [editorDelta, props.userName, handleNotify, closeEditorOverlay, receiveIDToken, getCurrentIDs, fetchDetailWidgetData, props.id]);

    // ESC Key schließt Overlay
    React.useEffect(() => {
        if(!showTestFreqOverlay && !showDeleteProductOverlay && !showDeleteTestOverlay && !showReloadOverlay) return;
        const onKey = (e) => {
            if(e.key === 'Escape') {
                if(showTestFreqOverlay) closeTestFreqOverlay();
                if(showDeleteProductOverlay) closeDeleteProductOverlay();
                if(showDeleteTestOverlay) closeDeleteTestOverlay();
                if(showReloadOverlay) closeReloadOverlay();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showTestFreqOverlay, showDeleteProductOverlay, showDeleteTestOverlay, showReloadOverlay, closeTestFreqOverlay, closeDeleteProductOverlay, closeDeleteTestOverlay, closeReloadOverlay]);

    // Autofokus auf Input beim Öffnen
    React.useEffect(() => {
        if(showTestFreqOverlay && testFreqInputRef.current){
            testFreqInputRef.current.focus();
            testFreqInputRef.current.select();
        }
        if(showDeleteProductOverlay && deletePwInputRef.current){
            deletePwInputRef.current.focus();
            deletePwInputRef.current.select();
        }
        if(showDeleteTestOverlay && deleteTestPwInputRef.current){
            deleteTestPwInputRef.current.focus();
            deleteTestPwInputRef.current.select();
        }
    }, [showTestFreqOverlay, showDeleteProductOverlay, showDeleteTestOverlay]);
    React.useEffect(() => {}, [showDeleteProductOverlay, showDeleteTestOverlay]);


    /**
     * Speichern (Panel 1): Formularwerte an Metadata API senden
     * Sammelt versteckte IDs und bearbeitbare Felder, baut flaches Payload
     * und aktualisiert lokal optimistisch nach Erfolg.
     */
    const handleSave = (detailInfo) => {
        if(!detailInfo){
            props.handleNotify && props.handleNotify({type:'alert', message:'Speichern fehlgeschlagen: keine Detaildaten vorhanden.'});
            return;
        }
        const formEl = detailFormRef.current;
        if(!formEl){
            props.handleNotify && props.handleNotify({type:'alert', message:'Speichern fehlgeschlagen: Formular nicht gefunden.'});
            return;
        }
        // Versteckte technische IDs + bearbeitbare Felder einsammeln
        const inputs = Array.from(formEl.querySelectorAll('input[data-key]'));
        const kv = inputs.reduce((acc, inp) => {
            const key = inp.getAttribute('data-key');
            if(key){
                acc[key] = inp.value;
            }
            return acc;
        }, {});
        const ids = getCurrentIDs();
        if(!ids){
            props.handleNotify && props.handleNotify({type:'alert', message:'Speichern abgebrochen: keine gültigen IDs.'});
            return;
        }
        const { productID, testID } = ids;
        const productName = kv.productName;
        const testName = kv.testName;
        const testOwner = kv.testOwner;
        // Validierung: min 2, max 45 Zeichen für Produkt/Test/Owner
        const isEmpty = (s) => typeof s !== 'string' || s.trim().length === 0;
        const tooShort = (s) => typeof s === 'string' && s.trim().length < 3;
        const tooLong = (s, max = 40) => typeof s === 'string' && s.trim().length > max;
        if(isEmpty(productName) || isEmpty(testName) || isEmpty(testOwner)){
            props.handleNotify && props.handleNotify({type:'alert', message:'Bitte alle Eingabefelder ausfüllen.'});
            return;
        }
        if(tooShort(productName) || tooLong(productName) || tooShort(testName) || tooLong(testName, 50) || tooShort(testOwner) || tooLong(testOwner)){
            props.handleNotify && props.handleNotify({type:'alert', message:'Produkt- und Owner-Namen 3–40 Zeichen, Test-Name 3–50 Zeichen.'});
            return;
        }
        const thresholdOkay = kv.thresholdOkay;
        const thresholdWarn = kv.thresholdWarn;
        const thresholdCrit = kv.thresholdCrit;
        // Threshold Validierung: min 1, max 4 Ziffern.
        // Bei AWS-Tests sind Thresholds im Detailwidget ausgeblendet und werden
        // unveraendert aus detailInfo im Payload mitgeschickt — Validierung uebersprungen.
        const isValidThreshold = (s) => typeof s === 'string' ? /^\d{1,4}$/.test(s.trim()) : false;
        if(isEditing && props.selectedGraph?.origin !== 'AWS') {
            if(!isValidThreshold(thresholdOkay) || !isValidThreshold(thresholdWarn) || !isValidThreshold(thresholdCrit)){
                props.handleNotify && props.handleNotify({type:'alert', message:'Thresholds müssen aus 1 bis 4 Ziffern bestehen.'});
                return;
            }
        }
        const token = receiveIDToken && receiveIDToken();
        // Debug Log des exakten Payloads (flach; bewusst auskommentiert)
        // eslint-disable-next-line no-console
        // console.log('[DetailWidget] POST /details/metadata payload', { productID, testID, userName: props.userName || '', productName, testName, testOwner, thresholdOkay, thresholdWarn, thresholdCrit });
        detailWidgetApi.updateMetadata(token, { productID, testID, userName: props.userName || '', productName, testName, testOwner, thresholdOkay, thresholdWarn, thresholdCrit })
            .then(resp => {
                // Optimistic Update lokal
                setDetailData(prev => {
                    if(!prev || !Array.isArray(prev.detailinfos) || prev.detailinfos.length === 0) return prev;
                    const clone = { ...prev };
                    clone.detailinfos = [...clone.detailinfos];
                    clone.detailinfos[0] = {
                        ...clone.detailinfos[0],
                        productName: productName ?? clone.detailinfos[0].productName,
                        testName: testName ?? clone.detailinfos[0].testName,
                        testOwner: testOwner ?? clone.detailinfos[0].testOwner,
                        thresholds: {
                            ...(clone.detailinfos[0].thresholds || {}),
                            okay: thresholdOkay ?? (clone.detailinfos[0].thresholds && clone.detailinfos[0].thresholds.okay),
                            warn: thresholdWarn ?? (clone.detailinfos[0].thresholds && clone.detailinfos[0].thresholds.warn),
                            crit: thresholdCrit ?? (clone.detailinfos[0].thresholds && clone.detailinfos[0].thresholds.crit)
                        }
                    };
                    return clone;
                });
                props.handleNotify && props.handleNotify({type:'success', message:'Metadaten gespeichert.'});
                setIsEditing(false);
                // Nach Speichern Panel 1: Reload-Overlay anbieten statt sofortigem Refresh
                openReloadOverlay();
            })
            .catch(() => {
                props.handleNotify && props.handleNotify({type:'alert', message:'Metadaten speichern fehlgeschlagen.'});
            });
    }

    // Manuelles Reload (Panels 1/2): aktualisiert Detaildaten
    const handleRefresh = () => {
        if(!props.selectedGraph){
            handleNotify && handleNotify({type:'alert', message:'Kein Graph ausgewählt. Refresh abgebrochen.'});
            return;
        }
        const match = drawnGraphs.find(g => g.productName === props.selectedGraph.productName && g.testName === props.selectedGraph.testName);
        if(!match){
            handleNotify && handleNotify({type:'alert', message:'Selektierter Graph nicht mehr vorhanden. Refresh abgebrochen.'});
            return;
        }
        const productID = match.productID;
        const testID = match.testID;
        if(!productID || !testID){
            handleNotify && handleNotify({type:'alert', message:'Refresh abgebrochen: fehlende IDs.'});
            return;
        }
        // Kurzer Ladezustand setzen (UI Feedback)
        setLoadReason('refresh');
        setIsLoaded(false);
        fetchDetailWidgetData(productID, testID, props.id).then(data => {
            if(data){
                // eslint-disable-next-line no-console
                // console.log('Detailwidget Refresh Dummy Daten neu geladen', data);
                handleNotify && handleNotify({type:'info', message:'Detaildaten aktualisiert.'});
                setLoadReason('initial');
            }
        });
    };

 
    /***********************************************************************************
     * DOM für das Konfigurationsmenü generieren 
     ***********************************************************************************/
    // ------------------------------------------------------------------------
    // Konfigurationsmenü (aktuell Platzhalter / rudimentär)
    // ------------------------------------------------------------------------
    const FillConfigMenu = () => {
        return(
            <div className="productWidget--configMenuWrap productWidgetConfig--Card">
                <div className="productWidget--HeaderWrap drag-handle productWidgetConfig--Header">
                    <div className="productWidgetConfig--HeaderTextWrap">
                        <span className="productWidgetConfig--HeaderMeta">PRODUKTDETAIL</span>
                        <span className="productWidgetConfig--HeaderTitle">Widget-Konfiguration</span>
                    </div>
                    <button
                        type="button"
                        className="productWidgetHeader--MenuBtn productWidgetConfig--CloseBtn"
                        onClick={() => toggleConfigMenuStatus()}
                        aria-label="Konfiguration schließen"
                        title="Schließen"
                    >
                        <FontAwesomeIcon icon="fa-solid fa-xmark" />
                    </button>
                </div>
                <div className="productWidgetConfig--ContentWrap productWidgetConfig--Body">
                    <div className="productWidgetConfig--EmptyState">
                        <FontAwesomeIcon icon="fa-solid fa-sliders" />
                        <div>Für dieses Widget sind aktuell keine Optionen verfügbar.</div>
                    </div>
                </div>
                <div className="productWidgetConfig--DeleteWrap productWidgetConfig--Footer">
                    <button
                        type="button"
                        className="productWidgetHeader--DeleteBtn productWidgetConfig--DeleteBtn"
                        onClick={() => props.handleDelete(props.id)}
                    >
                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                        <span>Widget entfernen</span>
                    </button>
                </div>
            </div>
        )
    }


    /***
     *     __   __            __   ___ ___       __       
     *    |  \ /  \  |\/|    |__) |__   |  |  | |__) |\ | 
     *    |__/ \__/  |  |    |  \ |___  |  \__/ |  \ | \| 
     *                                                    
     */

    // ------------------------------------------------------------------------
    // Panel Titel Mapping
    // ------------------------------------------------------------------------
    const panelTitles = {
        1: 'Testdetails - Infos',
        2: 'Testdetails - Jira Tickets',
        3: 'Testdetails - Test Klickweg',
        4: 'Testdetails - SOC ToDos',
        5: 'Testdetails - Admin Funktionen'
    };
    const currentTitle = panelTitles[activePanel] || 'Testdetails';
    // Ableitungen für Sichtbarkeit von Edit-Buttons abhängig von realen Inhalten
    const selectedMatch = props.selectedGraph ? drawnGraphs.find(g => g.productName === props.selectedGraph.productName && g.testName === props.selectedGraph.testName) : null;
    const hasPanel1Content = Boolean(selectedMatch && detailInfo);
    const hasKlickwegContent = React.useMemo(() => {
        const rawKW = detailData?.Klickweg;
        let opsArray = [];
        if(Array.isArray(rawKW)) {
            rawKW.forEach(entry => { if(entry && Array.isArray(entry.ops)) opsArray = entry.ops; });
        } else if(rawKW && Array.isArray(rawKW.ops)) {
            opsArray = rawKW.ops;
        }
        return Boolean(selectedMatch && isLoaded && Array.isArray(opsArray) && opsArray.length > 0);
    }, [detailData, selectedMatch, isLoaded]);
    const hasHandlungsContent = React.useMemo(() => {
        const rawHA = detailData?.Handlungsanweisung;
        let opsArray = [];
        if(Array.isArray(rawHA)) {
            rawHA.forEach(entry => { if(entry && Array.isArray(entry.ops)) opsArray = entry.ops; });
        } else if(rawHA && Array.isArray(rawHA.ops)) {
            opsArray = rawHA.ops;
        }
        return Boolean(selectedMatch && isLoaded && Array.isArray(opsArray) && opsArray.length > 0);
    }, [detailData, selectedMatch, isLoaded]);
    // Derive current match once for use in actions bar outside inner IIFE scroll region
    // Detail Info from loaded dummy JSON (first entry for now!)
    return(
        <>
            {configMenuStatus ?  <FillConfigMenu /> : null}
            {showTestFreqOverlay && createPortal(
                <div className="testFreqOverlay" role="dialog" aria-modal="true" aria-label="Testhäufigkeit anpassen" onClick={closeTestFreqOverlay}>
                    <div className="testFreqOverlayContent" onClick={(e) => e.stopPropagation()}>
                        <div className="testFreqOverlayHeader" id="testfreq-header">Testhäufigkeit anpassen</div>
                        <div className="testFreqOverlayBody">
                            <label className="testFreqOverlayLabel" htmlFor="testfreq-input">
                                <span className="testFreqOverlayLabelText">Intervall</span>
                                <div className="testFreqOverlayInputRow">
                                    <input
                                        id="testfreq-input"
                                        ref={testFreqInputRef}
                                        type="text"
                                        className="testFreqOverlayInput"
                                        value={testFreqValue}
                                        onChange={handleTestFreqChange}
                                        placeholder="Minuten"
                                        maxLength={4}
                                        aria-label="Testintervall in Minuten"
                                    />
                                    <span className="testFreqOverlayUnit">min.</span>
                                </div>
                            </label>
                            <label className="testFreqOverlayLabel" htmlFor="adminpw-input">
                                <span className="testFreqOverlayLabelText">Admin Passwort</span>
                                <div className="testFreqOverlayInputRow">
                                    <input
                                        id="adminpw-input"
                                        type={showAdminPassword ? 'text' : 'password'}
                                        className={"testFreqOverlayInput" + (adminPasswordValue.trim()==='' ? ' testFreqOverlayInput--invalid' : '')}
                                        value={adminPasswordValue}
                                        onChange={handleAdminPasswordChange}
                                        placeholder="Passwort"
                                        aria-label="Administratives Passwort"
                                    />
                                    <button
                                        type="button"
                                        className="testFreqOverlayPwToggle"
                                        onClick={toggleAdminPasswordVisibility}
                                        aria-label={showAdminPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                        title={showAdminPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                    >
                                        <FontAwesomeIcon icon={showAdminPassword ? 'fa-eye-slash' : 'fa-eye'} />
                                    </button>
                                </div>
                            </label>
                        </div>
                        <div className="testFreqOverlayActions">
                            <button type="button" className="detailWidgetActionBtn detailWidgetActionBtnSave" onClick={handleTestFreqSave}>Speichern</button>
                            <button type="button" className="detailWidgetActionBtn detailWidgetActionBtn--secondary" onClick={closeTestFreqOverlay}>Abbrechen</button>
                        </div>
                    </div>
                </div>, document.body)}
            {showDeleteProductOverlay && createPortal(
                <div className="testFreqOverlay" role="dialog" aria-modal="true" aria-label="Produkt löschen" onClick={closeDeleteProductOverlay}>
                    <div className="testFreqOverlayContent" onClick={(e) => e.stopPropagation()}>
                        <div className="testFreqOverlayHeader">Produkt löschen</div>
                        <div className="testFreqOverlayBody">
                            <div className="testFreqOverlayLabelText" style={{color:'var(--red)', fontWeight:'600', textAlign:'center', display:'flex', justifyContent:'center', alignItems:'center', gap:'12px'}}>
                                <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" style={{fontSize:'1.6em'}} />
                                <span style={{display:'flex', alignItems:'center'}}>
                                    Achtung folgendes Produkt wird unwiederruflich gelöscht:
                                </span>
                                <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" style={{fontSize:'1.6em'}} />
                            </div>
                            <div className="testFreqOverlayLabelText" style={{fontSize:'13px', fontWeight:'600', color:'var(--text-color)'}}>
                                {(detailInfo?.productName || props.selectedGraph?.productName || 'Unbekanntes Produkt')}
                            </div>
                            <div className="testFreqOverlayLabelText" style={{fontSize:'11px', opacity:.8}}>
                                Produkt ID: {(detailInfo?.productID || props.selectedGraph?.productID || 'unbekannte-ID')}
                            </div>
                            <label className="testFreqOverlayLabel" htmlFor="adminpw-delete-input">
                                <span className="testFreqOverlayLabelText">Admin Passwort</span>
                                <div className="testFreqOverlayInputRow">
                                    <input
                                        id="adminpw-delete-input"
                                        ref={deletePwInputRef}
                                        type={showAdminPassword ? 'text' : 'password'}
                                        className={"testFreqOverlayInput" + (adminPasswordValue.trim()==='' ? ' testFreqOverlayInput--invalid' : '')}
                                        value={adminPasswordValue}
                                        onChange={handleAdminPasswordChange}
                                        placeholder="Passwort"
                                        aria-label="Administratives Passwort"
                                    />
                                    <button
                                        type="button"
                                        className="testFreqOverlayPwToggle"
                                        onClick={toggleAdminPasswordVisibility}
                                        aria-label={showAdminPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                        title={showAdminPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                    >
                                        <FontAwesomeIcon icon={showAdminPassword ? 'fa-eye-slash' : 'fa-eye'} />
                                    </button>
                                </div>
                            </label>
                        </div>
                        <div className="testFreqOverlayActions">
                            <button type="button" className="detailWidgetActionBtn detailWidgetActionBtnSave" onClick={handleProductDelete}>Löschen</button>
                            <button type="button" className="detailWidgetActionBtn detailWidgetActionBtn--secondary" onClick={closeDeleteProductOverlay}>Abbrechen</button>
                        </div>
                    </div>
                </div>, document.body)}
            {showDeleteTestOverlay && createPortal(
                <div className="testFreqOverlay" role="dialog" aria-modal="true" aria-label="Test löschen" onClick={closeDeleteTestOverlay}>
                    <div className="testFreqOverlayContent" onClick={(e) => e.stopPropagation()}>
                        <div className="testFreqOverlayHeader">Test löschen</div>
                        <div className="testFreqOverlayBody">
                            <div className="testFreqOverlayLabelText" style={{color:'var(--red)', fontWeight:'600', textAlign:'center', display:'flex', justifyContent:'center', alignItems:'center', gap:'12px'}}>
                                <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" style={{fontSize:'1.6em'}} />
                                <span style={{display:'flex', alignItems:'center'}}>
                                    Achtung folgender Test wird unwiederruflich gelöscht:
                                </span>
                                <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" style={{fontSize:'1.6em'}} />
                            </div>
                            <div className="testFreqOverlayLabelText" style={{fontSize:'13px', fontWeight:'600', color:'var(--text-color)'}}>
                                {(detailInfo?.productName || props.selectedGraph?.productName || 'Unbekanntes Produkt')}
                            </div>
                            <div className="testFreqOverlayLabelText" style={{fontSize:'13px', fontWeight:'600', color:'var(--text-color)'}}>
                                {(detailInfo?.testName || props.selectedGraph?.testName || 'Unbekannter Test')}
                            </div>
                            <div className="testFreqOverlayLabelText" style={{fontSize:'11px', opacity:.8}}>
                                Test ID: {(props.selectedGraph?.testID || 'unbekannt')}
                            </div>
                            <label className="testFreqOverlayLabel" htmlFor="adminpw-test-delete-input">
                                <span className="testFreqOverlayLabelText">Admin Passwort</span>
                                <div className="testFreqOverlayInputRow">
                                    <input
                                        id="adminpw-test-delete-input"
                                        ref={deleteTestPwInputRef}
                                        type={showAdminPassword ? 'text' : 'password'}
                                        className={"testFreqOverlayInput" + (adminPasswordValue.trim()==='' ? ' testFreqOverlayInput--invalid' : '')}
                                        value={adminPasswordValue}
                                        onChange={handleAdminPasswordChange}
                                        placeholder="Passwort"
                                        aria-label="Administratives Passwort"
                                    />
                                    <button
                                        type="button"
                                        className="testFreqOverlayPwToggle"
                                        onClick={toggleAdminPasswordVisibility}
                                        aria-label={showAdminPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                        title={showAdminPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                                    >
                                        <FontAwesomeIcon icon={showAdminPassword ? 'fa-eye-slash' : 'fa-eye'} />
                                    </button>
                                </div>
                            </label>
                        </div>
                        <div className="testFreqOverlayActions">
                            <button type="button" className="detailWidgetActionBtn detailWidgetActionBtnSave" onClick={handleTestDelete}>Löschen</button>
                            <button type="button" className="detailWidgetActionBtn detailWidgetActionBtn--secondary" onClick={closeDeleteTestOverlay}>Abbrechen</button>
                        </div>
                    </div>
                </div>, document.body)}
            {showReloadOverlay && createPortal(
                <div className="savedOverlay" role="dialog" aria-modal="true" aria-label="Neu laden erforderlich" onClick={closeReloadOverlay}>
                    <div className="savedOverlayContent" onClick={(e) => e.stopPropagation()}>
                        <div className="savedOverlayHeader">
                            <div className="savedOverlayIcon" aria-hidden="true">
                                <FontAwesomeIcon icon="fa-solid fa-circle-check" />
                            </div>
                            <div className="savedOverlayHeaderText">
                                <div className="savedOverlayHeaderMeta">Metadaten aktualisiert</div>
                                <div className="savedOverlayHeaderTitle">Änderungen gespeichert</div>
                            </div>
                        </div>
                        <div className="savedOverlayBody">
                            <div>Die Stammdaten wurden erfolgreich gespeichert. Möchtest Du jetzt neu laden?</div>
                            <div className="savedOverlayBodyHint">
                                Das Neuladen stellt sicher, dass alle Metadaten (inkl. „Letzte Änderung") aktuell sind.
                            </div>
                        </div>
                        <div className="savedOverlayActions">
                            <button type="button" className="detailWidgetActionBtn detailWidgetActionBtn--secondary" onClick={closeReloadOverlay}>Später</button>
                            <button type="button" className="detailWidgetActionBtn detailWidgetActionBtnSave" onClick={() => window.location.reload()}>Jetzt neu laden</button>
                        </div>
                    </div>
                </div>, document.body)}
            {editorOverlayType && createPortal(
                (() => {
                    const isLogs = editorOverlayType === 'logs';
                    const productDisplay = detailInfo?.productName || props.selectedGraph?.productName || 'Unbekanntes Produkt';
                    const testDisplay = detailInfo?.testName || props.selectedGraph?.testName || 'Unbekannter Test';
                    const metaLabel = editorOverlayType === 'klickweg' ? 'KLICKWEG BEARBEITEN'
                                    : editorOverlayType === 'handlungs' ? 'HANDLUNGSANWEISUNG BEARBEITEN'
                                    : 'LOGS ANSEHEN';
                    const contextTitle = `${productDisplay} · ${testDisplay}`;
                    const ariaLabel = `${metaLabel} – ${contextTitle}`;
                    const subHeader = 'Bitte stelle sicher das Du das richtige Produkt und den richtigen Test ausgewählt hast, bevor Du Änderungen vornimmst.';
                    return (
                        <div className="quillEditOverlay" role="dialog" aria-modal="true" aria-label={ariaLabel}>
                            <div className="quillEditOverlayContent">
                                <div className="quillEditOverlayHeader">
                                    <div className="quillEditOverlayHeaderText">
                                        <span className="quillEditOverlayHeaderMeta">{metaLabel}</span>
                                        <span className="quillEditOverlayHeaderTitle">{contextTitle}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="quillEditOverlayCloseBtn"
                                        aria-label="Overlay schließen"
                                        title="Schließen"
                                        onClick={closeEditorOverlay}
                                    >
                                        <FontAwesomeIcon icon="fa-solid fa-xmark" />
                                    </button>
                                </div>
                                {!isLogs && (
                                    <div className="quillEditOverlaySubHeader">{subHeader}</div>
                                )}
                                <div className="quillEditEditorWrap">
                                    <ReactQuill
                                        theme="snow"
                                        readOnly={isLogs}
                                        value={editorDelta}
                                        modules={isLogs ? { toolbar: false } : editableQuillModules}
                                        onChange={(content, delta, source, editor) => {
                                            if(isLogs) return; // keine Änderungen erfassen im Log-Modus
                                            try { setEditorDelta(editor.getContents()); } catch(err) { /* ignore */ }
                                        }}
                                        className={isLogs ? 'detailPanelQuillReadOnly' : ''}
                                        style={{flex: '1 1 auto', display:'flex', flexDirection:'column', minHeight:0, height:'100%', width:'100%'}}
                                    />
                                </div>
                                <div className="quillEditOverlayActions">
                                    {editorOverlayType === 'klickweg' && (
                                        <button type="button" className="detailWidgetActionBtn detailWidgetActionBtnSave" onClick={saveKlickwegDelta}>Speichern</button>
                                    )}
                                    {editorOverlayType === 'handlungs' && (
                                        <button type="button" className="detailWidgetActionBtn detailWidgetActionBtnSave" onClick={saveHandlungsDelta}>Speichern</button>
                                    )}
                                    <button type="button" className="detailWidgetActionBtn detailWidgetActionBtn--secondary" onClick={closeEditorOverlay}>{isLogs ? 'Schließen' : 'Abbrechen'}</button>
                                </div>
                            </div>
                        </div>
                    );
                })(), document.body)}
            <div className="productWidget--HeaderWrap drag-handle">
                <div className="productWidgetHeader--Title">{currentTitle}</div>               
                <button
                    className="productWidgetHeader--MenuBtn"
                    onClick={() => toggleConfigMenuStatus()}
                >
                    <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" />
                </button>
            </div>
            <div className="detailWidget--ContentWrap">
                {/* Horizontale Panel Navigation */}
                <div className="detailWidgetPanelNav">
                    <div className="detailWidgetSidebarButtons detailWidgetSidebarButtons--horizontal">
                        <button
                            type="button"
                            className={"dwSidebarBtn" + (activePanel===1?" dwSidebarBtn--active":"")}
                            onClick={() => handleSwitchPanel(1)}
                            aria-label="Basis Informationen"
                            data-tooltip-id="detailwidget-panels"
                            data-tooltip-content="Infos / Stammdaten"
                        ><FontAwesomeIcon icon="fa-circle-info" /></button>
                        <button
                            type="button"
                            className={"dwSidebarBtn" + (activePanel===2?" dwSidebarBtn--active":"")}
                            onClick={() => handleSwitchPanel(2)}
                            aria-label="Panel 2 Jira Tickets"
                            data-tooltip-id="detailwidget-panels"
                            data-tooltip-content="Jira Tickets"
                        ><FontAwesomeIcon icon="fa-solid fa-ticket" /></button>
                        <button
                            type="button"
                            className={"dwSidebarBtn" + (activePanel===3?" dwSidebarBtn--active":"")}
                            onClick={() => handleSwitchPanel(3)}
                            disabled={props.selectedGraph?.origin === 'AWS'}
                            aria-disabled={props.selectedGraph?.origin === 'AWS'}
                            aria-label="Panel 3 Test Klickweg"
                            data-tooltip-id="detailwidget-panels"
                            data-tooltip-content={props.selectedGraph?.origin === 'AWS' ? 'Nicht verfügbar für Cloudwatch Alarme' : 'Test Klickweg'}
                        ><FontAwesomeIcon icon="fa-circle-nodes" /></button>
                        <button
                            type="button"
                            className={"dwSidebarBtn" + (activePanel===4?" dwSidebarBtn--active":"")}
                            onClick={() => handleSwitchPanel(4)}
                            aria-label="Panel 4 SOC ToDos"
                            data-tooltip-id="detailwidget-panels"
                            data-tooltip-content="SOC ToDos"
                        ><FontAwesomeIcon icon="fa-list-check" /></button>
                        <button
                            type="button"
                            className={"dwSidebarBtn" + (activePanel===5?" dwSidebarBtn--active":"")}
                            onClick={() => handleSwitchPanel(5)}
                            aria-label="Panel 5 Admin Funktionen"
                            data-tooltip-id="detailwidget-panels"
                            data-tooltip-content="Admin Funktionen"
                        ><FontAwesomeIcon icon="fa-screwdriver-wrench" /></button>
                    </div>
                    {/* Tooltip Mount */}
                    <Tooltip id="detailwidget-panels" place="top" float={true} className="detailWidgetPanelsTooltip" />
                </div>
                {
                    // ----------------------------------------------------------------
                    // Haupt-Render Switch: Ladezustand / Fehler / Inhalt
                    // ----------------------------------------------------------------
                    (() => {
                        if (isLoaded === false)
                            return(
                                <div className="productWidget--LoadingWrap">
                                    <p className="productWidget--LoadingWrapText DetailStartText">{loadReason === 'refresh' ? 'Lade Daten ...' : 'Bitte Info Button im Test klicken'}</p>
                                    {loadReason === 'refresh' && (
                                        <p><AppSpinner /></p>
                                    )}
                                </div>
                            )

                        if (hasError === true)
                            return <div className="productWidget--ErrorWrap">Error</div>
                        else 
                            return(
                                <div className="detailWidgetContent--DataWrap detailWidgetContent--SingleColumn" style={{display:'flex', flexDirection:'column'}}>
                                    <div className="detailWidgetGraphsList" style={{overflowY: (activePanel===3 || activePanel===4) ? 'hidden' : 'auto'}}>
                                    {props.selectedGraph ? (() => { // Inhalt nur wenn ein Graph selektiert ist
                                        const match = drawnGraphs.find(g => g.productName === props.selectedGraph.productName && g.testName === props.selectedGraph.testName);
                                        if(!match){
                                            return <div className="detailWidgetMissingGraphMsg" role="status">Ausgewählter Graph nicht mehr vorhanden.</div>
                                        }
                                        // Use detailInfo (dummy / backend) for panel 1 fields. Wait until fetch finished (isLoaded)
                                        // eslint-disable-next-line no-unused-expressions
                                        detailInfo;
                                        return (
                                            <div className="detailWidgetGraphsTable" style={{fontSize:'12px', display:'flex', flexDirection:'column', minHeight:'0'}}>
                                                {activePanel === 1 && ( // Panel 1: Testdetails & Schwellenwerte
                                                <>
                                                {detailInfo?.lastChanged && (
                                                    <div className="detailWidgetMetaLine" aria-label="Letzte Änderung">
                                                        <span className="detailWidgetMetaLineLabel">Letzte Änderung:</span>{' '}
                                                        <span className="detailWidgetMetaLineValue">{detailInfo.lastChanged}{detailInfo.lastChangedBy ? (' – ' + detailInfo.lastChangedBy) : ''}</span>
                                                    </div>
                                                )}
                                                <form ref={detailFormRef} className="detailFieldsForm" aria-label="Detaildaten des ausgewählten Performance Tests">
                                                    {/* Versteckte technische IDs für Save-Payload */}
                                                    <input type="hidden" value={detailInfo?.productID || match.productID || ''} data-key="productID" data-label="Product ID" />
                                                    <input type="hidden" value={detailInfo?.testID || match.testID || ''} data-key="testID" data-label="Test ID" />
                                                    <div className="detailField">
                                                        <label className="detailFieldLabel" htmlFor="dw-productName">Produkt Name</label>
                                                        <input id="dw-productName" name="productName" className={"detailFieldInput " + (isEditing ? "detailFieldInput--editMode" : "detailFieldInput--viewMode")} type="text" required={isEditing} value={isEditing ? editableValues.productName : (detailInfo?.productName || match.productName || '')} onChange={isEditing ? onEditableChange : undefined} disabled={!isEditing} data-key="productName" data-label="Produkt Name" maxLength={40} {...(isEditing ? { minLength: 3 } : {})} />
                                                    </div>
                                                    <div className="detailField">
                                                        <label className="detailFieldLabel" htmlFor="dw-testName">Test Name</label>
                                                        <input id="dw-testName" name="testName" className={"detailFieldInput " + (isEditing ? "detailFieldInput--editMode" : "detailFieldInput--viewMode")} type="text" required={isEditing} value={isEditing ? editableValues.testName : (detailInfo?.testName || match.testName || '')} onChange={isEditing ? onEditableChange : undefined} disabled={!isEditing} data-key="testName" data-label="Test Name" maxLength={50} {...(isEditing ? { minLength: 3 } : {})} />
                                                    </div>
                                                    <div className="detailField">
                                                        <label className="detailFieldLabel" htmlFor="dw-testOwner">Test Owner</label>
                                                        <input id="dw-testOwner" name="testOwner" className={"detailFieldInput " + (isEditing ? "detailFieldInput--editMode" : "detailFieldInput--viewMode")} type="text" required={isEditing} value={isEditing ? editableValues.testOwner : (detailInfo?.testOwner ?? '')} onChange={isEditing ? onEditableChange : undefined} disabled={!isEditing} data-key="testOwner" data-label="Test Owner" maxLength={40} {...(isEditing ? { minLength: 3 } : {})} />
                                                    </div>
                                                    {detailInfo?.thresholds && props.selectedGraph?.origin !== 'AWS' && (
                                                        <>
                                                        <div className="detailField">
                                                            <label className="detailFieldLabel" htmlFor="dw-thresholdOk">Threshold Okay (sek.)</label>
                                                            <input id="dw-thresholdOk" name="thresholdOkay" className={"detailFieldInput " + (isEditing ? "detailFieldInput--editMode" : "detailFieldInput--viewMode")} type="text" value={isEditing ? editableValues.thresholdOkay : (detailInfo.thresholds.okay ?? '')} onChange={isEditing ? onEditableChange : undefined} disabled={!isEditing} data-key="thresholdOkay" data-label="Threshold Okay (sek.)" maxLength={4} />
                                                        </div>
                                                        <div className="detailField">
                                                            <label className="detailFieldLabel" htmlFor="dw-thresholdWarn">Threshold Warn (sek.)</label>
                                                            <input id="dw-thresholdWarn" name="thresholdWarn" className={"detailFieldInput " + (isEditing ? "detailFieldInput--editMode" : "detailFieldInput--viewMode")} type="text" value={isEditing ? editableValues.thresholdWarn : (detailInfo.thresholds.warn ?? '')} onChange={isEditing ? onEditableChange : undefined} disabled={!isEditing} data-key="thresholdWarn" data-label="Threshold Warn (sek.)" maxLength={4} />
                                                        </div>
                                                        <div className="detailField">
                                                            <label className="detailFieldLabel" htmlFor="dw-thresholdCrit">Threshold Crit (sek.)</label>
                                                            <input id="dw-thresholdCrit" name="thresholdCrit" className={"detailFieldInput " + (isEditing ? "detailFieldInput--editMode" : "detailFieldInput--viewMode")} type="text" value={isEditing ? editableValues.thresholdCrit : (detailInfo.thresholds.crit ?? '')} onChange={isEditing ? onEditableChange : undefined} disabled={!isEditing} data-key="thresholdCrit" data-label="Threshold Crit (sek.)" maxLength={4} />
                                                        </div>
                                                        </>
                                                    )}
                                                    {detailInfo?.testinterval !== undefined && props.selectedGraph?.origin !== 'AWS' && (
                                                        <div className="detailField">
                                                            <label className="detailFieldLabel" htmlFor="dw-testinterval">Testintervall (min.)</label>
                                                            <input id="dw-testinterval" className="detailFieldInput detailFieldInputStatic" type="text" value={(detailInfo.testinterval ?? '')} disabled data-key="testinterval" data-label="Testintervall (min)" />
                                                        </div>
                                                    )}
                                                    {detailInfo?.avgDuration !== undefined && props.selectedGraph?.origin !== 'AWS' && (
                                                        <div className="detailField">
                                                            <label className="detailFieldLabel" htmlFor="dw-avgDuration">Durchschnittsdauer (sek.)</label>
                                                            <input id="dw-avgDuration" className="detailFieldInput detailFieldInputStatic" type="text" value={(detailInfo.avgDuration ?? '')} disabled data-key="avgDuration" data-label="Durchschnittsdauer (sek.)" />
                                                        </div>
                                                    )}
                                                    
                                                </form>
                                                </>
                                                )}
                                                {activePanel === 2 && ( // Panel 2: Jira Tickets Liste
                                                    <>
                                                        <div className="detailPanelContext detailPanelContext--fixed" aria-label="Ticket Kontext">
                                                            <span className="detailPanelContextValue">{(detailInfo?.productName || match.productName || 'Unbekanntes Produkt')}</span>
                                                        </div>
                                                        <div className="detailPanelTickets" aria-label="Zugehörige Tickets">
                                                            {Array.isArray(detailData?.Tickets) && detailData.Tickets.length > 0 ? (
                                                                detailData.Tickets.map(t => {
                                                                    const statusClass = "detailTicketStatus detailTicketStatus--" + normalizeTicketStatus(t.status);
                                                                    const WrapperTag = t.link ? 'a' : 'div';
                                                                    const wrapperProps = t.link ? { href: t.link, target: '_blank', rel: 'noopener noreferrer' } : {};
                                                                    return (
                                                                        <WrapperTag key={t.ticket} className="detailTicketItem detailTicketItem--clickable" {...wrapperProps}>
                                                                            <div className="detailTicketRow1">
                                                                                <span className="detailTicketKey" aria-label="Ticket Key">{t.ticket}</span>
                                                                                <span className="detailTicketSep">|</span>
                                                                                <span className="detailTicketDate" title="Erstellt">{t.aktualisiert}</span>
                                                                                <span className="detailTicketRow1Spacer" aria-hidden="true"></span>
                                                                                <span className={statusClass}>{t.status}</span>
                                                                            </div>
                                                                            <div className="detailTicketRow2">{t.title}</div>
                                                                        </WrapperTag>
                                                                    );
                                                                })
                                                            ) : (
                                                                <div className="detailTicketsEmpty">Keine Tickets vorhanden.</div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                                {activePanel === 3 && ( // Panel 3: Klickweg jetzt als Read-Only Quill wie Panel 4
                                                    <div className="detailPanelKlickweg detailPanelWithMeta" aria-label="Klickweg Panel">
                                                        {(() => {
                                                            const rawKW = detailData?.Klickweg;
                                                            let opsArray = [];
                                                            let metaObj = null;
                                                            if(Array.isArray(rawKW)) {
                                                                rawKW.forEach(entry => {
                                                                    if(entry && typeof entry === 'object') {
                                                                        if(Array.isArray(entry.ops)) {
                                                                            opsArray = entry.ops;
                                                                        } else if(entry.lastChanged || entry.lastChangedBy) {
                                                                            metaObj = entry;
                                                                        }
                                                                    }
                                                                });
                                                            } else if(rawKW && Array.isArray(rawKW.ops)) {
                                                                opsArray = rawKW.ops;
                                                            }
                                                            const productDisplay = (detailInfo?.productName || match.productName || 'Unbekanntes Produkt');
                                                            const testDisplay = (detailInfo?.testName || match.testName || 'Unbekannter Test');
                                                            const lastChanged = metaObj?.lastChanged || '';
                                                            const lastChangedBy = metaObj?.lastChangedBy || '';
                                                            const delta = { ops: Array.isArray(opsArray) ? opsArray : [] };
                                                            const metaBlocks = [];
                                                            if(lastChanged) {
                                                                metaBlocks.push(
                                                                    <div key="kw-lastchanged" className="detailWidgetMetaLine" aria-label="Letzte Änderung Klickweg">
                                                                        <span className="detailWidgetMetaLineLabel">Letzte Änderung:</span>{' '}
                                                                        <span className="detailWidgetMetaLineValue">{lastChanged}{lastChangedBy ? (' – ' + lastChangedBy) : ''}</span>
                                                                    </div>
                                                                );
                                                            }
                                                            metaBlocks.push(
                                                                <div key="kw-context" className="detailWidgetMetaLine" aria-label="Produkt/Test Kontext">
                                                                    <span className="detailWidgetMetaLineValue">{productDisplay} / {testDisplay}</span>
                                                                </div>
                                                            );
                                                            return (
                                                                <>
                                                                    <div className="detailPanelMeta">{metaBlocks}</div>
                                                                    <div className="detailPanelScroll" aria-label="Klickweg Inhalt">
                                                                        {delta.ops.length === 0 ? (
                                                                            <div className="detailWidgetMissingGraphMsg" role="status">Kein Klickweg vorhanden.</div>
                                                                        ) : (
                                                                            <ReactQuill
                                                                                theme="snow"
                                                                                readOnly={true}
                                                                                value={delta}
                                                                                modules={{ toolbar: false }}
                                                                                className="detailPanelQuillReadOnly"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                                {activePanel === 4 && ( // Panel 4: Handlungsanweisung als read-only Quill Canvas (Standard Snow Theme)
                                                    <div className="detailPanelHandlungs detailPanelWithMeta" aria-label="Handlungsanweisung Panel">
                                                        {(() => {
                                                            const rawHA = detailData?.Handlungsanweisung;
                                                            let opsArray = [];
                                                            let metaObj = null;
                                                            if(Array.isArray(rawHA)) {
                                                                rawHA.forEach(entry => {
                                                                    if(entry && typeof entry === 'object') {
                                                                        if(Array.isArray(entry.ops)) {
                                                                            opsArray = entry.ops;
                                                                        } else if(entry.lastChanged || entry.lastChangedBy) {
                                                                            metaObj = entry;
                                                                        }
                                                                    }
                                                                });
                                                            } else if(rawHA && Array.isArray(rawHA.ops)) {
                                                                opsArray = rawHA.ops;
                                                            }
                                                            const productDisplay = (detailInfo?.productName || match.productName || 'Unbekanntes Produkt');
                                                            const testDisplay = (detailInfo?.testName || match.testName || 'Unbekannter Test');
                                                            const lastChanged = metaObj?.lastChanged || '';
                                                            const lastChangedBy = metaObj?.lastChangedBy || '';
                                                            // Quill Delta Objekt vorbereiten
                                                            const delta = { ops: Array.isArray(opsArray) ? opsArray : [] };
                                                            const metaBlocks = [];
                                                            if(lastChanged) {
                                                                metaBlocks.push(
                                                                    <div key="ha-lastchanged" className="detailWidgetMetaLine" aria-label="Letzte Änderung Handlungsanweisung">
                                                                        <span className="detailWidgetMetaLineLabel">Letzte Änderung:</span>{' '}
                                                                        <span className="detailWidgetMetaLineValue">{lastChanged}{lastChangedBy ? (' – ' + lastChangedBy) : ''}</span>
                                                                    </div>
                                                                );
                                                            }
                                                            metaBlocks.push(
                                                                <div key="ha-context" className="detailWidgetMetaLine" aria-label="Produkt/Test Kontext">
                                                                    <span className="detailWidgetMetaLineValue">{productDisplay} / {testDisplay}</span>
                                                                </div>
                                                            );
                                                            return (
                                                                <>
                                                                    <div className="detailPanelMeta">{metaBlocks}</div>
                                                                    <div className="detailPanelScroll panelMinusMargin" aria-label="Handlungsanweisung Inhalt">
                                                                        {delta.ops.length === 0 ? (
                                                                            <div className="detailWidgetMissingGraphMsg" role="status">Keine Handlungsanweisung vorhanden.</div>
                                                                        ) : (
                                                                            <ReactQuill
                                                                                theme="snow"
                                                                                readOnly={true}
                                                                                value={delta}
                                                                                modules={{ toolbar: false }}
                                                                                className="detailPanelQuillReadOnly"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                                {activePanel === 5 && ( // Panel 5: Admin Bereich
                                                    <div className="detailPanelAdmin" aria-label="Admin Funktionen">
                                                        <div className="detailPanelContext detailPanelContext--admin" aria-label="Admin Kontext">
                                                            <span className="detailPanelContextValue">{(detailInfo?.productName || match.productName || 'Unbekanntes Produkt')} / {(detailInfo?.testName || match.testName || 'Unbekannter Test')}</span>
                                                        </div>
                                                        <div className="adminPanelActions" role="group" aria-label="Administrative Aktionen">
                                                            <button type="button" className="adminActionBtn" disabled={!match || props.selectedGraph?.origin === 'AWS'} aria-disabled={!match || props.selectedGraph?.origin === 'AWS'} title={props.selectedGraph?.origin === 'AWS' ? 'Nicht verfügbar für Cloudwatch Alarme' : 'Testhäufigkeit anpassen'} onClick={openTestFreqOverlay}>
                                                                <FontAwesomeIcon icon="fa-solid fa-clock-rotate-left" />
                                                                <span className="adminActionBtnLabel">Testhäufigkeit anpassen</span>
                                                            </button>
                                                            <button type="button" className="adminActionBtn" disabled={!match} aria-disabled={!match} title="Logs anzeigen (Dummy)" onClick={() => openEditorOverlay('logs')}>
                                                                <FontAwesomeIcon icon="fa-solid fa-file-lines" />
                                                                <span className="adminActionBtnLabel">Logs anzeigen</span>
                                                            </button>
                                                        </div>
                                                        <hr className="adminPanelDivider" />
                                                        <div className="adminPanelLinks" aria-label="Relevante externe Ressourcen">
                                                            {[
                                                                { href: 'https://github.com/freenet-group/synthetic-monitoring', label: 'Repository: synthetic-monitoring' },
                                                                { href: 'https://github.com/freenet-group/synthetic-monitoring-git', label: 'Repository: synthetic-monitoring-git' },
                                                                { href: 'https://dashboard.fndls-op-prod.k8s.freenet-group.de/#/workloads?namespace=e2e-selenium-prod', label: 'K8s Workloads (prod)' },
                                                                { href: 'https://dashboard.fndls-op-np.k8s.freenet-group.de/#/workloads?namespace=e2e-selenium-dev', label: 'K8s Workloads (dev)' },
                                                                { href: 'https://selenium-hub.fndls-op-np.k8s.freenet-group.de/ui/', label: 'Selenium Hub (dev)' },
                                                                { href: 'https://selenium-hub.fndls-op-prod.k8s.freenet-group.de/ui/', label: 'Selenium Hub (prod)' },
                                                                { href: 'https://freenetgroup.sharepoint.com/sites/IT_Operations/SitePages/Dokumentation-Cloudwatch.aspx', label: 'SharePoint: Cloudwatch Doku' }
                                                            ].map(link => (
                                                                <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className="adminResourceLink">
                                                                    <FontAwesomeIcon icon="fa-solid fa-arrow-up-right-from-square" className="adminResourceLinkIcon" />
                                                                    <span className="adminResourceLinkLabel">{link.label}</span>
                                                                </a>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })() : (
                                        <p>Kein Graph ausgewählt. Klick auf das Info Icon eines Graphen um Details zu sehen.</p>
                                    )}
                                    </div>
                                    <div className="detailWidgetActionsBar"> {/* Globale Aktionsleiste */}
                                        <div className="detailWidgetActionsBarPlaceholder">
                                            {/* Refresh Button nur anzeigen in Panel 1 und 2 (nicht in 3 Klickweg, 4 Handlungsanweisung, 5 Admin) */}
                                            {(activePanel === 1 || activePanel === 2) && (
                                                <button
                                                    type="button"
                                                    className="detailWidgetActionBtn"
                                                    onClick={handleRefresh}
                                                    disabled={!props.selectedGraph || !isLoaded}
                                                    aria-disabled={!props.selectedGraph || !isLoaded}
                                                    aria-label="Neu laden"
                                                    data-tooltip-id="detailwidget-actions"
                                                    data-tooltip-content="Neu laden"
                                                ><FontAwesomeIcon icon="fa-arrows-rotate" /></button>
                                            )}
                                            {/* Produkt/Test löschen Buttons jetzt in globaler Aktionsleiste wenn Panel 5 aktiv */}
                                            {activePanel === 5 && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="adminActionBtn adminActionBtn--danger adminActionBtn--toolbar"
                                                        disabled={!props.selectedGraph}
                                                        aria-disabled={!props.selectedGraph}
                                                        title="Produkt löschen (Dummy)"
                                                        onClick={openDeleteProductOverlay}
                                                    >
                                                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                                                        <span className="adminActionBtnLabel">Produkt löschen</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="adminActionBtn adminActionBtn--danger adminActionBtn--toolbar"
                                                        disabled={!props.selectedGraph}
                                                        aria-disabled={!props.selectedGraph}
                                                        title="Test löschen (Dummy)"
                                                        onClick={openDeleteTestOverlay}
                                                    >
                                                        <FontAwesomeIcon icon="fa-solid fa-trash-can" />
                                                        <span className="adminActionBtnLabel">Test löschen</span>
                                                    </button>
                                                </>
                                            )}
                                            {activePanel === 1 && hasPanel1Content && (
                                                <>
                                                    <button className={"detailWidgetActionBtn" + (isEditing ? " detailWidgetActionBtn--editing" : "" )} type="button" onClick={() => enableEditing(detailInfo)} disabled={isEditing} aria-disabled={isEditing}>Editieren</button>
                                                    {isEditing && (
                                                        <>
                                                            <button className="detailWidgetActionBtn" type="button" onClick={() => handleSave(detailInfo)} aria-label="Speichern" title="Speichern" data-tooltip-id="detailwidget-actions" data-tooltip-content="Speichern">
                                                                <FontAwesomeIcon icon="fa-solid fa-floppy-disk" />
                                                            </button>
                                                            <button className="detailWidgetActionBtn detailWidgetActionBtn--secondary" type="button" onClick={cancelEditing} aria-label="Abbrechen" title="Abbrechen" data-tooltip-id="detailwidget-actions" data-tooltip-content="Abbrechen">
                                                                <FontAwesomeIcon icon="fa-solid fa-xmark" />
                                                            </button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            {activePanel === 3 && hasKlickwegContent && (
                                                <button
                                                    type="button"
                                                    className="detailWidgetActionBtn"
                                                    onClick={() => openEditorOverlay('klickweg')}
                                                    disabled={!hasKlickwegContent}
                                                    aria-disabled={!hasKlickwegContent}
                                                    aria-label="Klickweg bearbeiten"
                                                    data-tooltip-id="detailwidget-actions"
                                                    data-tooltip-content="Klickweg bearbeiten"
                                                >Editieren</button>
                                            )}
                                            {activePanel === 4 && hasHandlungsContent && (
                                                <button
                                                    type="button"
                                                    className="detailWidgetActionBtn"
                                                    onClick={() => openEditorOverlay('handlungs')}
                                                    disabled={!hasHandlungsContent}
                                                    aria-disabled={!hasHandlungsContent}
                                                    aria-label="Handlungsanweisung bearbeiten"
                                                    data-tooltip-id="detailwidget-actions"
                                                    data-tooltip-content="Handlungsanweisung bearbeiten"
                                                >Editieren</button>
                                            )}
                                            <Tooltip id="detailwidget-actions" place="top" float={true} className="detailWidgetPanelsTooltip" />
                                        </div>
                                    </div>
                                </div>
                            )
                    })()
                }
            </div>
        </>
    )

}

export default memo(DetailWidget)
